import axios from 'axios';
import { getPrivateKey, setPrivateKey, getEncryptionKey, clearEncryptionKeys } from './crypto';
import { startKeySharing, askOtherTabs } from './keyShare';
import { leaveForElsewhere } from './quickExit';
import { openSealedTree, hasSealed } from './vault';
import { requestUnlock } from './unlock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token + acting-on-behalf-of header to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // When the user has switched into a shared account, every API call
  // needs the X-Acting-On-Behalf-Of header so the backend treats the
  // request as being made on behalf of the owner.
  try {
    const acting = sessionStorage.getItem('acting_on_behalf_of');
    if (acting) {
      const parsed = JSON.parse(acting);
      if (parsed?.owner_id) {
        config.headers['X-Acting-On-Behalf-Of'] = parsed.owner_id;
      }
    }
  } catch { /* ignore */ }
  return config;
});

const LOCKED_REASON = 'Some of your details are locked in this tab. Enter your password to open them.';
const ACTION_REASON = 'This needs your details unlocked first. Enter your password to carry on.';

// Endpoints whose server logic reads sealed details, so the key rides in
// their JSON body (never a URL or header). Without one the server answers
// 423 and the retry below asks the person to unlock first.
const KEY_BODY_PATHS = [
  /\/organisations\/[^/]+\/suggestions\/[^/]+\/confirm$/,
  /^\/organisations$/,
  /^\/organisations\/[^/]+$/,
  /^\/organisations\/[^/]+\/placements$/,
  /^\/organisations\/[^/]+\/placements\/[^/]+\/respite$/,
  /^\/organisations\/[^/]+\/respite\/[^/]+\/promote$/,
  /^\/sar\/generate$/,
  /^\/sar\/batch-generate$/,
  /^\/organisations\/[^/]+\/gaps$/,
];

// Writes that send back details this tab would have had to open first. From
// a tab without the key they'd carry blanks where the sealed values were, so
// they don't go: the person unlocks, the page reloads with the real values,
// and they save again. (The server also keeps any blob it gets back unopened.)
const SEALED_PROFILE_FIELDS = [
  'date_of_birth', 'previous_names', 'current_address', 'address_history', 'pronouns', 'gender',
  'demographics', 'respite_carers', 'siblings',
];
const needsOpenedDetails = (config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  if (method === 'put' && /^\/organisations\/[^/]+$/.test(url)) return true;
  if (method === 'put' && url === '/profile') {
    let body = config.data;
    if (typeof body === 'string') {
      try { body = JSON.parse(body); } catch { return false; }
    }
    return !!body && SEALED_PROFILE_FIELDS.some((f) => body[f] !== undefined);
  }
  return false;
};

// Another open tab may already be unlocked (lib/keyShare.js): ask once,
// before this tab's first request, so a new tab opens straight away.
startKeySharing({
  signedOut: () => {
    clearEncryptionKeys();
    if (!['/', '/login', '/register'].includes(window.location.pathname)) window.location.href = '/login';
  },
  quickExit: leaveForElsewhere,
});
let peerKey = null;
const keyFromOtherTabs = () => {
  if (!peerKey) {
    peerKey = localStorage.getItem('auth_token') && !getPrivateKey()
      ? askOtherTabs().then((key) => { if (key && !getPrivateKey()) setPrivateKey(key); })
      : Promise.resolve();
  }
  return peerKey;
};

const withKeyInBody = async (config) => {
  if (!getPrivateKey()) await keyFromOtherTabs();
  const url = config.url || '';
  if (needsOpenedDetails(config) && !getPrivateKey()) {
    await requestUnlock(LOCKED_REASON).then(() => window.location.reload()).catch(() => {});
    throw new axios.Cancel('Unlock first: this page was showing locked details');
  }
  const method = (config.method || 'get').toLowerCase();
  if (!['post', 'put'].includes(method) || !KEY_BODY_PATHS.some((rx) => rx.test(url))) return config;
  const key = getPrivateKey();
  if (!key || config.data instanceof FormData) return config;
  let body = config.data;
  if (typeof body === 'string') {
    try { body = JSON.parse(body); } catch { return config; }
  }
  config.data = { ...(body || {}), private_key: key };
  return config;
};
api.interceptors.request.use(withKeyInBody);


// Open sealed details in every response, in the browser (lib/vault.js). A
// tab without the key asks once, then reloads so the page fetches again.
api.interceptors.response.use(async (response) => {
  const data = response.data;
  if (!data || typeof data !== 'object' || data instanceof Blob || !hasSealed(data)) return response;
  const key = getPrivateKey();
  if (key) {
    await openSealedTree(data, key);
  } else {
    requestUnlock(LOCKED_REASON).then(() => window.location.reload()).catch(() => {});
  }
  return response;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (error.response?.status === 423 && config && !config._unlockRetried) {
      try {
        await requestUnlock(ACTION_REASON);
      } catch {
        return Promise.reject(error);
      }
      config._unlockRetried = true;
      return api(config);
    }
    // Only redirect on actual 401 errors, not network errors
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/register'];
      const requestUrl = error.config?.url || '';
      // /auth/unlock and /auth/recovery-code return 401 for an incorrect
      // password during an interactive re-prompt — that's a credential
      // validation failure, not session expiry, so don't sign the user out.
      const isAuthValidationCall = ['/auth/unlock', '/auth/recovery-code'].some((p) => requestUrl.includes(p));
      
      // Don't clear token or redirect if we're already on a public page
      if (!publicPaths.includes(currentPath) && !isAuthValidationCall) {
        console.log('Auth error 401, clearing session');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        clearEncryptionKeys();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Get the decryption key for file downloads.
 * Uses private key (RSA) if available, falls back to legacy symmetric key.
 */
const getDecryptionKey = () => {
  const privateKey = getPrivateKey();
  if (privateKey) {
    return privateKey;
  }
  // Fall back to legacy symmetric key
  const legacyKey = getEncryptionKey();
  if (legacyKey) {
    return legacyKey;
  }
  throw new Error('Encryption key not found. Please log in again.');
};

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  setupMfa: () => api.post('/auth/setup-mfa'),
  verifyMfa: (data) => api.post('/auth/verify-mfa', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  unlock: (data) => api.post('/auth/unlock', data),
  // Account recovery codes (the private key sealed under a one-time code)
  generateRecoveryCode: (password) => api.post('/auth/recovery-code', { password }),
  recoveryCodePdf: (recoveryCode) =>
    api.post('/auth/recovery-code/pdf', { recovery_code: recoveryCode }, { responseType: 'blob' }),
  recover: (data) => api.post('/auth/recover', data),
};

// Profile API
export const profileApi = {
  update: (data) => api.put('/profile', data),
  demographicsOptions: () => api.get('/profile/demographics-options'),
  export: (privateKey) => api.post(
    '/profile/export',
    { private_key: privateKey || null },
    { responseType: 'blob' }
  ),
  deleteAccount: () => api.delete('/profile/account'),
  // Avatar upload
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: () => api.delete('/profile/avatar'),
  myActivity: (limit = 100) => api.get('/profile/activity', { params: { limit } }),
  // Shared access (owner-side)
  listInvitations: () => api.get('/invitations'),
  createInvitation: (data) => api.post('/invitations', data),
  updateInvitation: (id, data) => api.put(`/invitations/${id}`, data),
  revokeInvitation: (id, data) => api.delete(`/invitations/${id}`, { data }),
  downloadAuthorisation: (id) => api.get(`/invitations/${id}/authorisation.pdf`, { responseType: 'blob' }),
  // Shared access (invitee-side)
  incomingInvitations: () => api.get('/invitations/incoming'),
  acceptInvitation: (id, invitationSecret) =>
    api.post(`/invitations/${id}/accept`, { invitation_secret: invitationSecret || null }),
  declineInvitation: (id) => api.post(`/invitations/${id}/decline`),
  leaveSharedAccount: (id) => api.post(`/invitations/${id}/leave`),
  accessSharedAccount: (id, data) => api.post(`/invitations/${id}/access`, data),
  activateOwnAccount: () => api.post('/questionnaire/activate-own-account'),
  // Upload uses server-side encryption - key type depends on sharing permission
  uploadIdDocument: (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    return api.post('/profile/id-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Download requires private key for zero-knowledge encrypted docs. POST so
  // the key travels in the body, never in the URL.
  downloadIdDocument: (docId) => {
    const privateKey = getDecryptionKey();
    return api.post(`/profile/id-document/${docId}/download`,
      { private_key: privateKey },
      { responseType: 'blob' });
  },
  deleteIdDocument: (docId) => api.delete(`/profile/id-document/${docId}`),
  // Filenames are only in each document's sealed metadata. Resolves to
  // {id: filename|null}; {} on failure, so the page falls back to the kind.
  idDocumentNames: async () => {
    try {
      const { data } = await api.post('/profile/id-documents/names', { private_key: getDecryptionKey() || null });
      return data.names || {};
    } catch {
      return {};
    }
  },
  // Re-encrypt ID documents when toggling sharing permission
  reencryptIdDocuments: (enableSharing, privateKey = null) => {
    const payload = { enable_sharing: enableSharing };
    if (privateKey) {
      payload.private_key = privateKey;
    }
    return api.post('/profile/id-documents/reencrypt', payload);
  },
};

// Questionnaire API
export const questionnaireApi = {
  saveCareHistory: (data) => api.post('/questionnaire/care-history', data),
  getCareHistory: () => api.get('/questionnaire/care-history'),
  skip: () => api.post('/questionnaire/skip'),
  // Same endpoint as skip() — it just flips onboarding_complete — but named
  // for the other call site: after the welcome screen's wizard adds the
  // user's first organisation, not only when they decline to add one.
  complete: () => api.post('/questionnaire/skip'),
};

// Organisations API
// The person's own words: comments, memories, highlights (routers/notes.py).
// Written sealed with their public key; opening needs the key.
const withKey = (body = {}) => {
  const privateKey = getDecryptionKey();
  if (!privateKey) return null;
  return { ...body, private_key: privateKey };
};

export const notesApi = {
  list: (filter = {}) => {
    const body = withKey(filter);
    if (!body) return Promise.resolve({ data: { notes: [], locked: true } });
    return api.post('/notes/list', body);
  },
  create: (note) => api.post('/notes', note),
  update: (id, note) => api.patch(`/notes/${id}`, note),
  remove: (id) => api.delete(`/notes/${id}`),
};

export const collectionsApi = {
  list: () => {
    const body = withKey();
    if (!body) return Promise.resolve({ data: { collections: [], locked: true } });
    return api.post('/collections/list', body);
  },
  create: (name, description = null) => api.post('/collections', { name, description }),
  rename: (id, name, description = null) => api.patch(`/collections/${id}`, { name, description }),
  remove: (id) => api.delete(`/collections/${id}`),
  addItem: (id, item) => api.post(`/collections/${id}/items`, item),
  removeItem: (id, itemId) => api.delete(`/collections/${id}/items/${itemId}`),
};

export const organisationsApi = {
  getAll: () => api.get('/organisations'),
  getEnriched: () => api.get('/organisations/enriched'),
  getDashboard: (orgId) => api.get(`/organisations/${orgId}/dashboard`),
  getRelated: (orgId) => api.get(`/organisations/${orgId}/related`),
  getIcon: (orgId) => api.get(`/organisations/${orgId}/icon`, { responseType: 'blob' }),
  add: (data) => api.post('/organisations', data),
  update: (orgId, data) => api.put(`/organisations/${orgId}`, data),
  deletePlacement: (orgId, placementId) => api.delete(`/organisations/${orgId}/placements/${placementId}`),
  delete: (orgId, { confirmActiveSar = false } = {}) =>
    api.delete(`/organisations/${orgId}`, { params: { confirm_active_sar: confirmActiveSar } }),
  updateStatus: (orgId, status) => api.put(`/organisations/${orgId}/status`, { org_id: orgId, status }),
  // options.alsoOrganisation: a children's home placement suggestion also
  // adds the home as its own organisation.
  confirmSuggestion: (orgId, suggestionId, options = {}) => api.post(
    `/organisations/${orgId}/suggestions/${suggestionId}/confirm`,
    options.alsoOrganisation ? { also_organisation: true } : undefined,
  ),
  rejectSuggestion: (orgId, suggestionId) => api.post(`/organisations/${orgId}/suggestions/${suggestionId}/reject`),
  clearAllSuggestions: () => api.post('/organisations/suggestions/clear-all'),
  // Placement-first flows (see backend services/placements.py)
  addPlacement: (orgId, data) => api.post(`/organisations/${orgId}/placements`, data),
  resolve: (orgId, data) => api.post(`/organisations/${orgId}/resolve`, data),
  // Organisation page: the filing cabinet, gaps and the org's requests
  getCabinet: (orgId) => api.get(`/organisations/${orgId}/cabinet`),
  // POST so the key rides in the body (KEY_BODY_PATHS): the findings can
  // then name the sealed placements they're about.
  getGaps: (orgId) => api.post(`/organisations/${orgId}/gaps`),
  setNation: (orgId, nation) => api.patch(`/organisations/${orgId}/nation`, { nation }),
  dismissFinding: (orgId, findingId, reason) => api.post(`/organisations/${orgId}/gaps/${findingId}/dismiss`, { reason }),
  restoreFinding: (orgId, findingId) => api.delete(`/organisations/${orgId}/gaps/${findingId}/dismiss`),
  // What we noticed was on screen: these stop counting as new (services/finding_seen.py).
  markFindingsSeen: (orgId, findingIds) => api.post(`/organisations/${orgId}/findings/seen`, { finding_ids: findingIds }),
  // Findings from inside the records (framing, allegations, timing) — the
  // server decrypts this organisation's records with the key, computes and
  // returns; nothing is stored. Resolves to no findings when locked.
  // The page was opened: clears the organisation's "something new" dot.
  markViewed: (orgId) => api.post(`/organisations/${orgId}/viewed`),
  // A closer read by the AI of one organisation's whole file, on request.
  // A closer read of the whole file runs as a job (a big file takes several
  // AI calls): start it, then poll until it's done. Resolves with the
  // finished result, like any other call; `onProgress({done, steps})`
  // reports how far through it is.
  reviewFile: async (orgId, onProgress, { pollMs = 3000 } = {}) => {
    const privateKey = getDecryptionKey();
    if (!privateKey) return { data: { findings: [], locked: true } };
    const start = await api.post(`/organisations/${orgId}/findings/review`, { private_key: privateKey });
    const jobId = start.data?.job_id;
    if (!jobId) return start;
    for (;;) {
      await new Promise((r) => setTimeout(r, pollMs));
      const r = await api.get(`/organisations/${orgId}/findings/review/${jobId}`);
      if (r.data?.state === 'done') return r;
      if (onProgress) onProgress({ done: r.data?.done || 0, steps: r.data?.steps || 0 });
    }
  },
  // "Your time with …" — written automatically after analysis, stored
  // encrypted; the status is plaintext, opening it needs the key.
  getSummaryStatus: (orgId) => api.get(`/organisations/${orgId}/summary`),
  openSummary: (orgId) => {
    const privateKey = getDecryptionKey();
    if (!privateKey) return Promise.resolve({ data: { locked: true } });
    return api.post(`/organisations/${orgId}/summary`, { private_key: privateKey });
  },
  refreshSummary: (orgId) => {
    const privateKey = getDecryptionKey();
    if (!privateKey) return Promise.resolve({ data: { locked: true } });
    return api.post(`/organisations/${orgId}/summary/refresh`, { private_key: privateKey });
  },
  getRecordFindings: (orgId) => {
    const privateKey = getDecryptionKey();
    if (!privateKey) return Promise.resolve({ data: { findings: [], locked: true } });
    return api.post(`/organisations/${orgId}/findings`, { private_key: privateKey });
  },
  // "Your time here": who the records name and when, records per home.
  // Decrypted in memory server-side; nothing stored.
  getProfile: (orgId) => {
    const privateKey = getDecryptionKey();
    if (!privateKey) return Promise.resolve({ data: { locked: true } });
    return api.post(`/organisations/${orgId}/profile`, { private_key: privateKey });
  },
  getSars: (orgId) => api.get(`/organisations/${orgId}/sars`),
  // Short breaks (respite) on a placement
  attachRespite: (orgId, placementId, data) => api.post(`/organisations/${orgId}/placements/${placementId}/respite`, data),
  detachRespite: (orgId, placementId, carerId) => api.delete(`/organisations/${orgId}/placements/${placementId}/respite/${carerId}`),
  promoteRespiteCarer: (orgId, carerId) => api.post(`/organisations/${orgId}/respite/${carerId}/promote`),
  downloadDocumentsZip: (orgId) => {
    const privateKey = getDecryptionKey();
    return api.post(`/organisations/${orgId}/documents/download-zip`,
      { private_key: privateKey },
      { responseType: 'blob' });
  },
};

// SAR API
export const sarApi = {
  generate: (data) => api.post('/sar/generate', data),
  composeFollowUp: (organisationId, organisationName, reasons, extraText) =>
    api.post('/sar/follow-up/compose', {
      organisation_id: organisationId, organisation_name: organisationName,
      reasons, extra_text: extraText,
    }),
  batchGenerate: (organisationIds) => api.post('/sar/batch-generate', { organisation_ids: organisationIds }),
  batchPreview: () => api.get('/sar/batch-preview'),
  getAll: () => api.get('/sar'),
  getOne: (id) => api.get(`/sar/${id}`),
  update: (id, letterContent) => {
    const formData = new FormData();
    formData.append('letter_content', letterContent);
    return api.put(`/sar/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadAll: () => {
    return api.get('/sar/download-all/zip', { responseType: 'blob' });
  },
  flagRefusal: (id, value, notes) =>
    api.post(`/sar/${id}/flag/refusal`, { value, notes }),
  flagRedactions: (id, value, notes) =>
    api.post(`/sar/${id}/flag/redactions`, { value, notes }),
  requestHelp: (id, notes) =>
    api.post(`/sar/${id}/help`, { value: true, notes }),
};

// Admin: SAR help queue
export const adminSarHelpApi = {
  list: (resolved = false) => api.get('/admin/sar-help', { params: { resolved } }),
  getOne: (id) => api.get(`/admin/sar-help/${id}`),
  resolve: (id, note) => api.post(`/admin/sar-help/${id}/resolve`, { note }),
};

// Feature ideas ("got an idea?" sidebar box)
export const featureIdeasApi = {
  submit: (text) => api.post('/feature-ideas', { text }),
};

// Admin: feature ideas
export const adminFeatureIdeasApi = {
  list: (status = 'open') => api.get('/admin/feature-ideas', { params: { status } }),
  complete: (id) => api.post(`/admin/feature-ideas/${id}/complete`),
  reopen: (id) => api.post(`/admin/feature-ideas/${id}/reopen`),
};

// Email API
export const emailApi = {
  sendSar: (sarId, includeIdDocuments = false) => {
    const payload = { sar_id: sarId, include_id_documents: includeIdDocuments };
    // Include private key if sending ID documents (for decryption before sending)
    if (includeIdDocuments) {
      payload.private_key = getDecryptionKey();
    }
    return api.post('/email/send-sar', payload);
  },
  getThread: (sarId) => api.get(`/email/thread/${sarId}`),
  getPendingActions: () => api.get('/email/pending-actions'),
  getPendingIdRequests: () => api.get('/email/pending-id-requests'),
  sendIdToAllPending: () => {
    // Requires private key to decrypt documents before sending
    const privateKey = getDecryptionKey();
    return api.post('/email/send-id-to-all-pending', { private_key: privateKey });
  },
  sendIdResponse: (emailId) => {
    // Manual, per-request send — works whether or not automatic ID sharing
    // is on, so this is also the fallback when it should have been
    // automatic but wasn't.
    const privateKey = getDecryptionKey();
    return api.post(`/email/send-id-response/${emailId}`, { private_key: privateKey });
  },
  markActioned: (emailId) => api.post(`/email/${emailId}/mark-actioned`),
  sendReply: (sarId, replyToEmailId, body) => {
    const formData = new FormData();
    formData.append('sar_id', sarId);
    formData.append('reply_to_email_id', replyToEmailId);
    formData.append('body', body);
    return api.post('/email/reply', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Deadlines API
export const deadlinesApi = {
  getAll: () => api.get('/email/deadlines'),
  getUpcoming: () => api.get('/email/deadlines/upcoming'),
  checkAndSendReminders: () => api.post('/email/deadlines/check-and-send-reminders'),
};

// Notifications API
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markActioned: (notificationId) => api.put(`/notifications/${notificationId}/actioned`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
};

// Documents API
export const documentsApi = {
  // Upload uses server-side public key encryption - no key needed from client
  upload: (file, organisationId, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organisation_id', organisationId);
    formData.append('document_type', documentType);
    return api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: () => api.get('/documents'),
  // Filename, type and size are sealed under the user's key, so GET
  // /documents doesn't carry them. Resolves to {id: {original_filename,
  // content_type, file_size}} — {} with no key or on failure, leaving the
  // rows unnamed rather than the page broken. Merge with mergeDocumentNames.
  names: async (ids) => {
    const privateKey = getDecryptionKey();
    if (!privateKey || !ids?.length) return {};
    try {
      const { data } = await api.post('/documents/names', { private_key: privateKey, ids });
      return data.names || {};
    } catch {
      return {};
    }
  },
  getOne: (id) => api.get(`/documents/${id}`),
  // Download requires private key for decryption. POST so the key travels in
  // the body, never in the URL.
  download: (id) => {
    const privateKey = getDecryptionKey();
    return api.post(`/documents/${id}/download`,
      { private_key: privateKey },
      { responseType: 'blob' });
  },
  // Text preview of Office/email/plain-text files, split into the same
  // pages the analysis pipeline saw. Same key-in-body rule as download.
  previewPages: (id) => {
    const privateKey = getDecryptionKey();
    return api.post(`/documents/${id}/preview-pages`, { private_key: privateKey });
  },
  delete: (id) => api.delete(`/documents/${id}`),
  // "Read" = a person opened the raw file (or dismissed the prompt to) —
  // separate from analysis. markViewed is called once the viewer opens.
  markViewed: (id) => api.post(`/documents/${id}/viewed`),
  dismissRead: (id) => api.post(`/documents/${id}/dismiss-read`),
};

/** `docs` with the names from documentsApi.names filled in (empty values skipped). */
export const mergeDocumentNames = (docs, names) =>
  (docs || []).map((d) => {
    const n = names?.[d.id];
    if (!n) return d;
    return { ...d, ...Object.fromEntries(Object.entries(n).filter(([, v]) => v != null)) };
  });

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Canonical Organisations API (autocomplete + stats summary)
export const canonicalOrgsApi = {
  search: (q, type) => api.get('/canonical-orgs/search', { params: { q, type: type || undefined } }),
  get: (id) => api.get(`/canonical-orgs/${id}`),
  getStatsSummary: (id) => api.get(`/canonical-orgs/${id}/stats-summary`),
  create: (data) => api.post('/canonical-orgs', data),
};

// Admin API
export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  leagueTable: () => api.get('/admin/league-table'),
  orgDemographicBreakdown: (orgId, dimension) =>
    api.get(`/admin/league-table/${orgId}/demographics`, { params: { dimension } }),
  demographicsSummary: () => api.get('/admin/demographics/summary'),
  listOrgs: (params) => api.get('/admin/organisations', { params }),
  getOrg: (id) => api.get(`/admin/organisations/${id}`),
  updateOrg: (id, data) => api.put(`/admin/organisations/${id}`, data),
  verifyOrg: (id) => api.post(`/admin/organisations/${id}/verify`),
  rejectOrg: (id) => api.post(`/admin/organisations/${id}/reject`),
  candidates: () => api.get('/admin/candidates'),
  addAlias: (id, alias) => api.post(`/admin/organisations/${id}/aliases`, { alias }),
  removeAlias: (id, alias) => api.delete(`/admin/organisations/${id}/aliases`, { data: { alias } }),
  // Merge
  mergePreview: (sourceId, targetId) =>
    api.get('/admin/merge/preview', { params: { source_id: sourceId, target_id: targetId } }),
  mergeExecute: (data) => api.post('/admin/merge', data),
  mergeRestore: (sourceId) => api.post(`/admin/merge/${sourceId}/restore`),
  mergeRecent: () => api.get('/admin/merge/recent'),
  // Libraries
  listDocTypes: (params) => api.get('/admin/document-types', { params }),
  createDocType: (data) => api.post('/admin/document-types', data),
  updateDocType: (id, data) => api.put(`/admin/document-types/${id}`, data),
  deleteDocType: (id) => api.delete(`/admin/document-types/${id}`),
  approveDocType: (id, data) => api.post(`/admin/document-types/pending/${id}/approve`, data || {}),
  rejectDocType: (id) => api.post(`/admin/document-types/pending/${id}/reject`),
  listLibraryTags: (params) => api.get('/admin/library-tags', { params }),
  createLibraryTag: (data) => api.post('/admin/library-tags', data),
  updateLibraryTag: (id, data) => api.put(`/admin/library-tags/${id}`, data),
  deleteLibraryTag: (id) => api.delete(`/admin/library-tags/${id}`),
  approveLibraryTag: (id, data) => api.post(`/admin/library-tags/pending/${id}/approve`, data || {}),
  rejectLibraryTag: (id) => api.post(`/admin/library-tags/pending/${id}/reject`),
  // Analysis
  analysisOverview: () => api.get('/admin/analysis/overview'),
  analysisJobs: () => api.get('/admin/analysis/jobs'),
  cancelAnalysisJob: (docId) => api.post(`/admin/analysis/jobs/${docId}/cancel`),
  listPrompts: () => api.get('/admin/analysis/prompts'),
  getPrompt: (processId) => api.get(`/admin/analysis/prompts/${processId}`),
  promptHistory: (processId) => api.get(`/admin/analysis/prompts/${processId}/history`),
  savePrompt: (processId, data) => api.post(`/admin/analysis/prompts/${processId}`, data),
  restorePrompt: (processId, version) => api.post(`/admin/analysis/prompts/${processId}/restore/${version}`),
  testPrompt: (processId, formData) => api.post(
    `/admin/analysis/prompts/${processId}/test`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  flaggedRuns: (processId) => api.get('/admin/analysis/flagged-runs', { params: { process_id: processId } }),
  // AI provider settings
  listProviders: () => api.get('/admin/settings/providers'),
  createProvider: (data) => api.post('/admin/settings/providers', data),
  updateProvider: (id, data) => api.put(`/admin/settings/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/admin/settings/providers/${id}`),
  testProvider: (id) => api.post(`/admin/settings/providers/${id}/test`),
  providerModels: (id) => api.get(`/admin/settings/providers/${id}/models`),
  listProcessAIConfig: () => api.get('/admin/settings/process-ai-config'),
  updateProcessAIConfig: (processId, data) => api.put(`/admin/settings/process-ai-config/${processId}`, data),
  // Activity logs
  activityActionTypes: () => api.get('/admin/activity-logs/action-types'),
  listActivityLogs: (params) => api.get('/admin/activity-logs', { params }),
  userActivityDetail: (userId, params) => api.get(`/admin/activity-logs/users/${userId}`, { params }),
  // Security events (audit log + database/host monitoring)
  securitySummary: () => api.get('/admin/security/summary'),
  securityEvents: (params) => api.get('/admin/security/events', { params }),
  acknowledgeSecurityEvent: (eventId, note) =>
    api.post(`/admin/security/events/${eventId}/acknowledge`, { note: note || null }),
  acknowledgeAllSecurityEvents: ({ up_to_seq, actions, note }) =>
    api.post('/admin/security/events/acknowledge-all', { up_to_seq, actions, note }),
  verifyAuditChain: () => api.post('/admin/security/verify-chain'),
  unlockUser: (userId) => api.post(`/admin/users/${userId}/unlock`),
  // Infrastructure — analysis worker fleet
  listAnalysisWorkers: () => api.get('/admin/analysis-workers'),
  createWorkerEnrollCode: () => api.post('/admin/analysis-workers/enroll-codes'),
  approveAnalysisWorker: (workerId) => api.post(`/admin/analysis-workers/${workerId}/approve`),
  revokeAnalysisWorker: (workerId) => api.post(`/admin/analysis-workers/${workerId}/revoke`),
  renameAnalysisWorker: (workerId, label) => api.patch(`/admin/analysis-workers/${workerId}`, { label }),
  deleteAnalysisWorker: (workerId) => api.delete(`/admin/analysis-workers/${workerId}`),
  requestAnalysisWorkerUpdate: (workerId) => api.post(`/admin/analysis-workers/${workerId}/request-update`),
  updateAnalysisWorkerSettings: (workerId, settings) => api.patch(`/admin/analysis-workers/${workerId}/settings`, settings),
  // Infrastructure — storage backend + self stats
  getStorageSettings: () => api.get('/admin/infrastructure/storage'),
  updateStorageSettings: (data) => api.put('/admin/infrastructure/storage', data),
  testStorageSettings: (data) => api.post('/admin/infrastructure/storage/test', data),
  apiSelfStats: () => api.get('/admin/infrastructure/self-stats'),
};

// Document analysis (user-side)
export const documentsAnalysisApi = {
  analyse: (docId, privateKeyPem) => api.post(`/documents/${docId}/analyse`, { private_key_pem: privateKeyPem || null }),
  analyseAllUnanalysed: (privateKeyPem, organisationId) =>
    api.post('/documents/analyse-all-unanalysed', { private_key_pem: privateKeyPem || null, organisation_id: organisationId || null }),
  log: (docId) => api.get(`/documents/${docId}/analysis-log`),
  batchStatus: (batchId) => api.get(`/documents/analysis-batch/${batchId}`),
};

// Support organisations (user-readable active list)
export const supportOrgsApi = {
  list: () => api.get('/support-organisations'),
};

// Admin support orgs CRUD
export const adminSupportOrgsApi = {
  list: (params) => api.get('/admin/support-organisations', { params }),
  create: (data) => api.post('/admin/support-organisations', data),
  update: (id, data) => api.put(`/admin/support-organisations/${id}`, data),
  delete: (id) => api.delete(`/admin/support-organisations/${id}`),
};

// Records ("My Life") API
export const recordsApi = {
  // Plaintext skeleton of every record (type, organisations, year, flags)
  // — no key needed, so it comes back instantly and My Life can lay out
  // placeholder cards before anything is decrypted.
  index: () => api.get('/records/index'),
  // Decrypt records. `ids` limits the batch (lazy loading); omit for all.
  list: (filters = null, ids = null, q = null) => {
    const privateKey = getDecryptionKey();
    return api.post('/records/list', { private_key_pem: privateKey, filters, ids, q: q || null });
  },
  get: (recordId) => {
    const privateKey = getDecryptionKey();
    return api.post(`/records/${recordId}`, { private_key_pem: privateKey });
  },
  facets: () => api.get('/records/facets/options'),
  radarPending: () => api.get('/records/radar/pending'),
  radarRun: () => {
    const privateKey = getDecryptionKey();
    return api.post('/records/radar/run', { private_key_pem: privateKey });
  },
};

// Public verification (unauthenticated)
export const publicApi = {
  verifyAuthorisation: (reference) => api.get(`/verify/${reference}`),
};

export default api;
// Coming-soon interest list (public) + admin management
export const earlyAccessApi = {
  status: () => api.get('/early-access/status'),
  registerInterest: (data) => api.post('/early-access', data),
  inviteLookup: (token) => api.get(`/early-access/invite/${token}`),
  adminList: () => api.get('/early-access/admin/list'),
  adminExportCsv: () => api.get('/early-access/admin/export.csv', { responseType: 'blob' }),
  adminMarkInvited: (id) => api.post(`/early-access/admin/${id}/mark-invited`),
};

// Public donation page (unauthenticated). No admin surface by design — see
// backend/app/routers/donations.py: GoCardless's own dashboard is the ledger.
export const donationsApi = {
  status: () => api.get('/donations/status'),
  create: (data) => api.post('/donations/create', data),
  confirm: (data) => api.post('/donations/confirm', data),
};
